import {
  istTeam,
  pulsAbschnittWechsel,
  pulsOhneAbgeschlossene,
  pulsSchritt,
  pulsUhrZiel,
  type PulsAbschnitt,
  type PulsAktion,
  type PulsBlock,
  type PulsStand,
  type PulsTermin,
  type PulsUhr,
  type TaskStatus,
} from '@netcon-work/shared'
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { heuteSchluessel } from './markierung'
import { sitzungQuery, termineHeuteQuery } from './queries'
import { trpc } from './trpc'

/**
 * **Der Fokus-Puls im Browser** ([Z-22](../../../../docs/phasen/zusatz-22-fokus-puls.md)).
 *
 * Gebaut wie die Markierung (`markierung.ts`): eine Abfrage, ein Schritt, optimistisch
 * (Leitplanke 1). Was ein Schritt bewirkt, sagt `pulsSchritt` aus `shared` voraus – dieselbe
 * Funktion, mit der der Server festschreibt.
 *
 * **Kein Live-Kanal**, wie bei der Markierung: Der Puls ist privat. Die Uhr läuft aus den
 * gespeicherten Zeitpunkten ohne Server weiter. Damit ein Wechsel am anderen Gerät ankommt,
 * fragt die Abfrage nach, solange ein Puls läuft – und beim Zurückkehren in den Tab ohnehin.
 */

export interface PulsLage {
  stand: PulsStand | null
  abschnitte: PulsAbschnitt[]
}

/** Mitternacht des Lesenden – „heute" ist seine Frage, nicht die des Servers. */
const mitternacht = (tag: string) => new Date(`${tag}T00:00:00`).toISOString()

export const pulsQuery = (tag: string) =>
  queryOptions({
    queryKey: ['puls', tag],
    queryFn: (): Promise<PulsLage> => trpc.puls.stand.query({ seit: mitternacht(tag) }),
    staleTime: 10_000,
    refetchInterval: (q) => (q.state.data?.stand ? 30_000 : false),
  })

/** Mein Puls und meine Abschnitte von heute. Für Mit-Owner und Gäste leer, ohne Aufruf. */
export function usePuls(): PulsLage & { geladen: boolean } {
  const { data: sitzung } = useQuery(sitzungQuery)
  const imTeam = sitzung ? istTeam(sitzung.role) : false
  const { data } = useQuery({ ...pulsQuery(heuteSchluessel()), enabled: imTeam })
  return useMemo(
    () => ({ stand: data?.stand ?? null, abschnitte: data?.abschnitte ?? [], geladen: !!data }),
    [data],
  )
}

/** Stand und Abschnitte nach einem Schritt – dieselbe Rechnung wie `festschreiben` im Server. */
function lageNach(lage: PulsLage, neu: PulsStand | null, jetzt: string): PulsLage {
  const wechsel = pulsAbschnittWechsel(lage.stand, neu)
  let abschnitte = lage.abschnitte
  if (wechsel.schliessen) {
    abschnitte = abschnitte.map((a) => (a.bis === null ? { ...a, bis: jetzt } : a))
  }
  if (wechsel.oeffnen) {
    abschnitte = [...abschnitte, { taskId: wechsel.oeffnen, von: jetzt, bis: null }]
  }
  return { stand: neu, abschnitte }
}

/**
 * **Ein Status wurde gesetzt – lässt der Puls die Aufgabe los?** Für `useStatusSetzen`: Wer im
 * Aufgabendetail erledigt, sieht die Uhr sofort gehen, nicht erst mit der nächsten Abfrage. Der
 * Server räumt beim nächsten Lesen genauso auf (`pulsOhneAbgeschlossene`).
 */
export function pulsNachStatus(qc: QueryClient, taskId: string, status: TaskStatus): void {
  const jetzt = new Date().toISOString()
  qc.setQueriesData<PulsLage>({ queryKey: ['puls'] }, (lage) => {
    if (!lage?.stand) return lage
    const neu = pulsOhneAbgeschlossene(
      lage.stand,
      (id) => (id === taskId ? status : undefined),
      jetzt,
    )
    return neu === lage.stand ? lage : lageNach(lage, neu, jetzt)
  })
}

/** Ein Schritt am Puls – sofort sichtbar, festgeschrieben im Hintergrund. */
export function usePulsSchritt() {
  const qc = useQueryClient()
  const schluessel = pulsQuery(heuteSchluessel()).queryKey

  return useMutation({
    mutationFn: (aktion: PulsAktion) => trpc.puls.schritt.mutate(aktion),
    onMutate: async (aktion) => {
      await qc.cancelQueries({ queryKey: schluessel })
      const vorher = qc.getQueryData<PulsLage>(schluessel)
      const jetzt = new Date().toISOString()
      qc.setQueryData<PulsLage>(schluessel, (alt) => {
        const lage = alt ?? { stand: null, abschnitte: [] }
        return lageNach(lage, pulsSchritt(lage.stand, aktion, jetzt), jetzt)
      })
      return vorher
    },
    onError: (_fehler, _a, vorher) => qc.setQueryData(schluessel, vorher),
    /** Wie die Markierung: danach neu holen – die Zeitpunkte des Servers gelten. */
    onSettled: () => qc.invalidateQueries({ queryKey: ['puls'] }),
  })
}

/** Graph liefert Ortszeit ohne Versatz und mit sieben Nachkommastellen (wie `TermineHeute`). */
function alsIso(wert: string): string | null {
  const d = new Date(wert.replace(/\.\d+$/, ''))
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** Die heutigen Termine in der Form, die `shared` rechnet – leer ohne Kalenderverbindung. */
export function usePulsTermine(): PulsTermin[] {
  const { data } = useQuery(termineHeuteQuery)
  return useMemo(() => {
    const termine: PulsTermin[] = []
    for (const t of data?.termine ?? []) {
      if (t.abgesagt) continue
      const beginn = alsIso(t.beginn)
      const ende = alsIso(t.ende)
      if (!beginn || !ende) continue
      termine.push({
        beginn,
        ende,
        betreff: t.betreff,
        ganztaegig: t.ganztaegig,
        zeigtAls: t.zeigtAls,
      })
    }
    return termine
  }, [data])
}

/** Der Block für eine Uhr – „bis Termin" sucht den nächsten Termin, jetzt. */
export function usePulsBlock(): (uhr: PulsUhr) => PulsBlock {
  const termine = usePulsTermine()
  return useCallback((uhr: PulsUhr) => pulsUhrZiel(uhr, termine, Date.now()), [termine])
}

/**
 * **Wohin „Zum Puls" führt** ([Z-24](../../../../docs/phasen/zusatz-24-my-work.md) AP 24.6) – in die
 * Ansicht, in der man den Puls zuletzt bedient hat: der Circle in „Mein Tag" oder „My Work". Je
 * Gerät im `localStorage`; ohne Speicher bleibt es beim Circle wie bisher.
 */
const PULS_ORT = 'netcon-work:puls-ort'
export type PulsOrt = '/' | '/my-work'

export function pulsOrtMerken(ort: PulsOrt): void {
  try {
    window.localStorage.setItem(PULS_ORT, ort)
  } catch {
    // Nicht merken können heißt nur: die Marke führt in den Circle.
  }
}

export function pulsOrt(): PulsOrt {
  try {
    return window.localStorage.getItem(PULS_ORT) === '/my-work' ? '/my-work' : '/'
  } catch {
    return '/'
  }
}
